import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { Typography, makeStyles, createStyles, Link } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import { IStoreState } from "../../Store";
import { signIn, signOut } from "./actions";
import { ThunkDispatch } from "redux-thunk";
import { ITheme } from "../../Layout/themes";
import { IRemoteData, IAuth, IEnvironment } from "../../../interface/IShareInfo";
import {
  validateEmail,
  validatePassword,
  validatorEmpty
} from "../../../commons/utils/validations";
import { ErrorBar } from "../../Ui/ErrorBar";

export interface IProps extends RouteComponentProps {
  signin: (email: string, password: string) => void;
  resetAuth: () => void;
  auth: IRemoteData<IAuth>;
  env: IEnvironment;
}

interface IStyleProps {
  signinBtnDisabled: boolean;
}

interface State {
  email: string;
  password: string;
  showPassword: boolean;
  emailInvalid: boolean;
  passwordInvalid: boolean;
  signinBtnDisabled: boolean;
  env: any;
}

const useStyles = makeStyles(({ palette, breakpoints }: ITheme) => {
  return createStyles({
    root: {
      paddingTop: 20,
      [breakpoints.down("sm")]: {
        paddingTop: 10
      }
    },
    title: {
      textAlign: "center",
      color: "#001828"
    },
    subTitle: {
      textAlign: "center",
      color: "#171717",
      marginTop: 20,
      marginBottom: 20
    },
    fieldsBox: {
      marginTop: 20
    },
    requiredText: {
      fontSize: "1rem",
      color: palette.text.primary
    },
    btnBox: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 40,
      marginTop: 20,
      [breakpoints.down("sm")]: {
        flexDirection: "column",
        marginBottom: 34
      }
    },
    forgotPasswordLink: {
      display: "flex",
      alignItems: "center",
      [breakpoints.down("sm")]: {
        order: 2,
        marginTop: 32,
        justifyContent: "center"
      }
    },
    linkText: {
      fontWeight: 600,
      cursor: "pointer"
    },
    forgotPasswordText: {
      fontWeight: 600,
      [breakpoints.down("sm")]: {
        order: 2,
        marginTop: 32
      }
    },
    rememberSigninBtnBox: {
      display: "flex",
      [breakpoints.down("sm")]: {
        order: 1,
        flexDirection: "column",
        width: "100%"
      }
    },
    noAccountBox: {
      display: "flex",
      height: 60,
      justifyContent: "center",
      alignItems: "center",
      borderTop: "1px solid #dbdbdb"
    },
    noAccountLink: {
      textDecoration: "none",
      display: "flex",
      justifyContent: "center",
      width: "100%"
    },
    signInButton: ({ signinBtnDisabled }: IStyleProps) => ({
      width: 108,
      height: 40,
      borderRadius: 5,
      boxShadow: signinBtnDisabled ? "none" : "0 2px 4px 0 rgba(0, 0, 0, 0.3)",
      backgroundColor: signinBtnDisabled
        ? palette.disabled.main
        : palette.success.main,
      "&:hover": {
        backgroundColor: palette.success.dark
      },
      [breakpoints.down("sm")]: {
        width: "100%"
      }
    }),
    signInButtonText: ({ signinBtnDisabled }: State) => ({
      color: signinBtnDisabled
        ? palette.disabled.contrastText
        : palette.primary.contrastText,
      textTransform: "none"
    }),
    checkboxBox: {
      // "& svg": {
      //   fontSize: 20
      // }
    },
    checkboxLabel: {
      color: palette.text.secondary
    },
    passwordInputField: {
      marginTop: 20
    },
    textFieldInput: {
      fontSize: "16px"
    },
    textFieldInputLabel: {
      fontSize: "1rem"
    },
    textFieldInputShowText: {
      color: palette.primary.main,
      fontWeight: 600,
      cursor: "pointer"
    }
  });
});

const validators = {
  email: validateEmail,
  password: validatePassword,
  showPassword: validatorEmpty,
  emailInvalid: validatorEmpty,
  passwordInvalid: validatorEmpty,
  signinBtnDisabled: validatorEmpty
};

export const SignInComponent = ({
  signin,
  resetAuth,
  auth,
  history,
  location,
  env
}: IProps) => {
  const [values, setValues] = useState<State>({
    email: "",
    password: "",
    showPassword: false,
    emailInvalid: false,
    passwordInvalid: false,
    signinBtnDisabled: true,
    env: env
  });
  const classes = useStyles({ signinBtnDisabled: values.signinBtnDisabled });

  useEffect(() => {
    if (auth.data && auth.data.token) {
      const keyIndex = location.search.indexOf("mobiUrl=");

      if (keyIndex > -1) {
        const newSearch = `${location.search.slice(
          0,
          keyIndex - 1
        )}?${location.search.slice(keyIndex)}`;
        history.push(newSearch.replace("?redirectUrl=", ""));
      } else {
        history.push("/");
      }
    } else {
      if (auth.error) {
        resetAuth();
      }
    }

    return () => {
      if (auth.error) {
        resetAuth();
      }
    };
  }, [auth.data.token]);

  useEffect(() => {
    if (auth.error) {
      window.scroll(0, 0);
    }
  }, [auth.error]);

  const handleChange = (prop: keyof State) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValues = {
      ...values,
      [prop]: event.target.value,
      [`${prop}Invalid`]: !validators[prop](event.target.value)
    };

    const newSigninBtnDisabled = !(
      validateEmail(newValues.email) && validatePassword(newValues.password)
    );

    setValues({
      ...newValues,
      signinBtnDisabled: newSigninBtnDisabled
    });
  };

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword
    });
  };

  const handleSignIn = () => {
    signin(values.email, values.password);
  };

  const handleHaveNoAccount = () => {
    resetAuth();
    history.push(`/signup${location.search}`);
  };

  return (
    <form className={classes.root}>
      <Typography variant="h1" className={classes.title}>
        Sign In
      </Typography>
      <Typography variant="body1" className={classes.subTitle}>
        You need to verify your identity to share and accept a ticket
      </Typography>
      {auth.error && <ErrorBar message={auth.error.message} />}
      <div className={classes.fieldsBox}>
        <TextField
          id="email"
          type="email"
          label="Email*"
          placeholder="Email*"
          fullWidth
          variant="outlined"
          helperText="*Required"
          error={values.emailInvalid}
          value={values.email}
          onChange={handleChange("email")}
          inputProps={{
            className: classes.textFieldInput
          }}
          InputLabelProps={{
            classes: {
              root: classes.textFieldInputLabel
            }
          }}
          FormHelperTextProps={{
            classes: {
              root: classes.requiredText
            }
          }}
        />
        <TextField
          autoComplete="on"
          id="password"
          label="Password*"
          placeholder="Password*"
          fullWidth
          variant="outlined"
          type={values.showPassword ? "text" : "password"}
          value={values.password}
          error={values.passwordInvalid}
          onChange={handleChange("password")}
          className={classes.passwordInputField}
          inputProps={{
            className: classes.textFieldInput
          }}
          InputLabelProps={{
            classes: {
              root: classes.textFieldInputLabel
            }
          }}
          InputProps={{
            endAdornment: (
              <Typography
                id="toggleMe"
                variant="h5"
                className={classes.textFieldInputShowText}
                onClick={handleClickShowPassword}
              >
                {values.showPassword ? "Hide" : "Show"}
              </Typography>
            )
          }}
          helperText="*Required"
          FormHelperTextProps={{
            classes: {
              root: classes.requiredText
            }
          }}
        />
      </div>
      <div className={classes.btnBox}>
        <Link
          component="a"
          href={env.FORGOT_PASSWORD_URL}
          target="_blank"
          rel="noreferrer"
          variant="h5"
          className={classes.forgotPasswordText}
        >
          Forgot password?
        </Link>

        <div className={classes.rememberSigninBtnBox}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  classes={{
                    root: classes.checkboxBox
                  }}
                />
              }
              classes={{
                label: classes.checkboxLabel
              }}
              label="Remember me"
            />
          </FormGroup>
          <Button
            className={classes.signInButton}
            onClick={handleSignIn}
            disabled={values.signinBtnDisabled}
          >
            <Typography variant="body1" className={classes.signInButtonText}>
              Sign in
            </Typography>
          </Button>
        </div>
      </div>
      <div className={classes.noAccountBox}>
        <Link
          component="div"
          href="/signup"
          variant="h5"
          className={classes.linkText}
          onClick={handleHaveNoAccount}
        >
          Don't have an account?
        </Link>
      </div>
    </form>
  );
};

const mapStateToProps = (state: IStoreState) => {
  return {
    auth: state.authData,
    env: state.env
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, any>) => {
  return {
    signin: (email: string, password: string) =>
      dispatch(signIn(email, password)),
    resetAuth: () => dispatch(signOut())
  };
};

export const SignIn = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(SignInComponent));
